<template>
    <v-form v-model="valid">
        <v-row dense>
            <v-col cols="12">
                <v-select
                    v-model="packageProgramId"
                    :items="packagePrograms"
                    :label="$t('tours.select_program')"
                    :loading="packageProgramsLoading"
                    :disabled="packageProgramsLoading"
                    item-value="id"
                    item-title="name"
                    clearable
                    @update:model-value="onProgramChange"
                />
            </v-col>
            <v-col cols="12">
                <v-select
                    v-model="packageCountryId"
                    :items="packageCountriesFiltered"
                    :label="$t('tours.select_country')"
                    :loading="packageCountriesLoading"
                    :disabled="packageCountriesLoading || !packageCountriesFiltered.length"
                    item-value="id"
                    item-title="name"
                    clearable
                    @update:model-value="onCountryChange"
                />
            </v-col>
            <v-col cols="12">
                <v-select
                    v-model="packageCityId"
                    :items="packageCitiesFiltered"
                    :label="$t('tours.select_destination')"
                    :loading="packageCitiesLoading"
                    :disabled="packageCitiesLoading || !packageCountryId || !packageCitiesFiltered.length"
                    item-value="id"
                    item-title="name"
                    clearable
                    @update:model-value="onCityChange"
                />
            </v-col>
            <v-col cols="12">
                <v-select
                    v-model="searchRequest.packageTourId"
                    :items="packageTours"
                    :label="$t('tours.select_package')"
                    :rules="[requiredRule('tours.package')]"
                    :loading="packageToursLoading"
                    :disabled="packageToursLoading"
                    item-value="packageTourId"
                    item-title="name"
                />
            </v-col>
            <v-col cols="12">
                <city-airport-autocomplete
                    v-if="isShowFlyFrom"
                    v-model="departure"
                    :rules="[requiredRule('fly_from')]"
                    :label="`${$t('fly_from')}*`"
                    :single-line="false"
                    filled
                    class="mb-6 city-airport-field"
                    @update:model-value="updateSearchRequest"
                />
            </v-col>
            <v-col cols="12">
                <date-picker v-model="searchRequest.startDateFrom" :label="$t('start_date')" :clearable="false" />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <rooms-select-list
                    v-model="rooms"
                    :max-tourists="$conf.public.account.newReservationRequestMaxTourists"
                    class="mt-5"
                    @change-age="onRoomsChange"
                />
            </v-col>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import {appInstance} from '@/utils/app-accessor'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import RoomsSelectList from '@/components/search/forms/RoomsSelectList.vue'
    import {searchRequest} from '@/utils/tours/tours-blank-states'
    import CityAirportAutocomplete from '~/components/flights/search/forms/CityAirportAutocomplete.vue'

    @Component({components: {CityAirportAutocomplete, RoomsSelectList, DatePicker}})
    export class SelectPackageTourForm extends mixins(FormValidationRulesMixin) {
        @VModel() valid
        @Prop({required: true}) searchRequest

        packagePrograms = []
        packageProgramId = null
        packageProgramsLoading = false

        packageToursLoading = false

        packageCountries = []
        packageCountryId = null
        packageCountriesLoading = false

        packageCities = []
        packageCitiesLoading = false
        packageCityId = null

        packageDestinations = []
        rooms = (() => {
            const {adults, childrenAges} = searchRequest()
            return [{adults, childrenAges}]
        })()
        departure = null

        @Watch('rooms', {deep: true})
        onRoomsChange() {
            this.searchRequest.rooms = this.rooms.map(room => hotelsRuntimeStore.roomQueryString(room))
        }

        @Watch('isShowFlyFrom')
        onPackageTourChange() {
            if (!this.isShowFlyFrom) {
                this.searchRequest.departureAirportCode = null
            }
        }

        async mounted() {
            await this.loadPackagePrograms()
            await this.loadPackageCountries()
            await toursRuntimeStore.loadPackageTours()
            this.rooms = hotelsRuntimeStore.searchRequestTouristsJSON(this.searchRequest)
        }

        async loadPackagePrograms() {
            try {
                this.packageProgramsLoading = true
                this.packagePrograms = await appInstance.$api.packagePrograms.get({
                    packageLink: true,
                })
                this.packageProgramsLoading = false
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async loadPackageCountries() {
            try {
                this.packageCountriesLoading = true

                this.packageCountries = []
                this.packageCities = []
                this.packageCityId = null

                this.packageCountries = (
                    await this.$api.locations.get({
                        limitCountries: 300,
                        productType: 'PACKAGE',
                    })
                ).countries?.sort((a, b) => a.name?.localeCompare(b.name))
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.packageCountriesLoading = false
            }
        }

        async loadPackageCities() {
            try {
                this.packageCities = []
                this.packageCitiesLoading = true
                this.packageCities = (
                    await this.$api.locations.get({
                        limitCities: 300,
                        productType: 'PACKAGE',
                        ...(this.packageCountryId && {countryId: this.packageCountryId}),
                    })
                ).cities?.sort((a, b) => a.name?.localeCompare(b.name))
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.packageCitiesLoading = false
            }
        }

        async loadPackageTours() {
            this.packageToursLoading = true

            const request = {
                ...(this.packageCountryId && {countryId: this.packageCountryId}),
                ...(this.packageCityId && {cityId: this.packageCityId}),
                ...(this.packageProgramId && {programId: this.packageProgramId}),
            }
            if (this.packageProgramId) {
                this.searchRequest.packageTourId = null
            }
            await toursRuntimeStore.loadPackageTours(request)

            this.packageToursLoading = false
        }

        async onProgramChange(id) {
            this.packageCountryId = null
            this.packageCityId = null

            await this.loadPackageTours(id)
        }

        onCountryChange() {
            this.loadPackageCities()
            this.loadPackageTours()
        }

        onCityChange() {
            this.loadPackageTours()
        }

        updateSearchRequest(val) {
            if (!val) return
            if (val.type === 'city') {
                delete this.searchRequest.departureAirportCode
                this.searchRequest.departureCityId = val.cityId
            } else {
                this.searchRequest.departureAirportCode = val.iataCode
                this.searchRequest.departureCityId = null
            }
        }

        get packageTours() {
            return toursRuntimeStore.packageTours.sort((a, b) => a.name?.localeCompare(b.name))
        }

        get availableLocations() {
            return this.packageTours.reduce((arr, el) => {
                arr.push(...el.locations)

                return arr
            }, [])
        }

        get packageCountriesFiltered() {
            return this.packageCountries.filter(c =>
                this.availableLocations.find(location => location.countryId === c.id)
            )
        }

        get packageCitiesFiltered() {
            return this.packageCities.filter(c => this.availableLocations.find(location => location.cityId === c.id))
        }

        get isShowFlyFrom() {
            const selectedPackageTour = this.packageTours.find(
                p => p.packageTourId === this.searchRequest.packageTourId
            )

            return selectedPackageTour?.itinerary?.find(el => el.serviceType === 'FLIGHT')
        }
    }

    export default SelectPackageTourForm
</script>

<style lang="scss" scoped>
    :deep(.city-airport-field .v-input__slot) {
        padding: 0 !important;
    }
</style>
